import type { MessageListener, Platform, StorageChangeListener, Unsubscribe } from './types';
import { onWebStorageChanged } from './webKvStore';

/**
 * Web/PWA implementation of the platform adapters (ARCH-005, ADR-012).
 * There is no background worker on the web: bus messages are delivered
 * in-page (where the web runtime handles what the extension background used
 * to serve) and mirrored to other tabs over a BroadcastChannel.
 */

const BUS_CHANNEL = 'tpl-message-bus';

// Injected by vite.web.config.ts `define`; guarded so the extension builds
// (which also typecheck this file but never bundle it) don't need the define.
declare const __APP_VERSION__: string | undefined;
declare const __APP_DESCRIPTION__: string | undefined;

const busListeners = new Set<MessageListener>();
const busChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(BUS_CHANNEL) : null;

function deliver(message: any): void {
  busListeners.forEach(listener => {
    try {
      listener(message);
    } catch {
      // One faulty listener must not break the others.
    }
  });
}

if (busChannel) {
  busChannel.onmessage = event => deliver(event.data);
}

async function sendMessage(message: any): Promise<unknown> {
  // Unlike chrome.runtime.sendMessage, deliver to THIS page's listeners too:
  // on the web the handlers that the extension background served (e.g.
  // BILLING_REFRESH → premium reconcile) run in-page.
  deliver(message);
  busChannel?.postMessage(message);
  return undefined;
}

function subscribeToMessages(listener: MessageListener): Unsubscribe {
  busListeners.add(listener);
  return () => busListeners.delete(listener);
}

function onStorageChanged(listener: StorageChangeListener): Unsubscribe {
  return onWebStorageChanged(listener);
}

async function openExternalUrl(url: string): Promise<void> {
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function downloadFile(options: {
  blob: Blob;
  filename: string;
  subdirectory?: string;
}): Promise<number | undefined> {
  // Browsers don't let pages pick a Downloads subdirectory; the filename is
  // the best we can do.
  const url = URL.createObjectURL(options.blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = options.filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return undefined;
}

function getAppInfo(): { version: string; description: string } {
  return {
    version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '',
    description: typeof __APP_DESCRIPTION__ !== 'undefined' ? __APP_DESCRIPTION__ : '',
  };
}

export function createWebPlatform(): Platform {
  return {
    capabilities: {
      persistentStorage: typeof indexedDB !== 'undefined',
      extensionSurfaces: false,
      // settings.html is a build input of the web target too (account
      // management and sign-out live there).
      settingsPage: true,
      fileDownloads: true,
      storeReview: false,
      upgradeFlow: true,
    },
    storage: { onChanged: onStorageChanged },
    bus: { send: sendMessage, subscribe: subscribeToMessages },
    actions: { openExternalUrl, downloadFile, getAppInfo },
  };
}
